import { bedingungErfuellt, type Antworten } from "./bedingungen";
import type { Funnel, Schritt } from "./schema";

/**
 * Der Ablauf durch den Funnel.
 *
 * Welcher Schritt als Nächstes kommt, hängt von den bisherigen Antworten ab.
 * Diese Funktionen sind die einzige Stelle, an der das entschieden wird, und
 * sie sind rein: gleiche Eingabe, gleiches Ergebnis, kein Zustand. Damit sind
 * sie vollständig testbar, ohne dass ein Browser läuft.
 */

/** Ist dieser Schritt bei diesen Antworten überhaupt sichtbar? */
export function schrittSichtbar(schritt: Schritt, antworten: Antworten): boolean {
  if (!schritt.condition) return true;
  return bedingungErfuellt(schritt.condition, antworten);
}

/** Alle Schritte, die bei diesen Antworten sichtbar sind, in Reihenfolge. */
export function sichtbareSchritte(funnel: Funnel, antworten: Antworten): Schritt[] {
  return funnel.steps.filter((s) => schrittSichtbar(s, antworten));
}

export function schrittFinden(funnel: Funnel, id: string): Schritt | undefined {
  return funnel.steps.find((s) => s.id === id);
}

/**
 * Der nächste Schritt nach dem angegebenen.
 *
 * Zuerst greifen die Sprungregeln des Schritts, von oben nach unten, die erste
 * zutreffende gewinnt. Gibt es keine passende Regel, geht es zum nächsten
 * Schritt in der Reihenfolge, dessen Bedingung erfüllt ist.
 *
 * `null` bedeutet: Der Funnel ist zu Ende.
 */
export function naechsterSchritt(
  funnel: Funnel,
  aktuelleId: string,
  antworten: Antworten,
): Schritt | null {
  const aktuell = schrittFinden(funnel, aktuelleId);
  if (!aktuell) return null;

  if (aktuell.next) {
    for (const regel of aktuell.next) {
      if (!regel.if || bedingungErfuellt(regel.if, antworten)) {
        const ziel = schrittFinden(funnel, regel.goto);
        // Ein Sprung auf einen Schritt, dessen eigene Bedingung nicht erfüllt
        // ist, würde eine Frage zeigen, die für diesen Nutzer nicht gedacht
        // ist. In dem Fall wird der Sprung übersprungen und der nächste
        // sichtbare Schritt danach genommen.
        if (ziel && schrittSichtbar(ziel, antworten)) return ziel;
        if (ziel) return naechsterNachPosition(funnel, ziel.id, antworten);
        return null;
      }
    }
  }

  return naechsterNachPosition(funnel, aktuelleId, antworten);
}

function naechsterNachPosition(
  funnel: Funnel,
  abId: string,
  antworten: Antworten,
): Schritt | null {
  const position = funnel.steps.findIndex((s) => s.id === abId);
  if (position === -1) return null;

  for (let i = position + 1; i < funnel.steps.length; i++) {
    const kandidat = funnel.steps[i];
    if (kandidat && schrittSichtbar(kandidat, antworten)) return kandidat;
  }

  return null;
}

/** Der erste Schritt, der bei diesen Antworten sichtbar ist. */
export function ersterSchritt(funnel: Funnel, antworten: Antworten = {}): Schritt | null {
  return funnel.steps.find((s) => schrittSichtbar(s, antworten)) ?? null;
}

/**
 * Der Weg, den ein Nutzer mit diesen Antworten genommen hat.
 *
 * Wird für die Rückwärtsnavigation gebraucht: Der vorherige Schritt ist nicht
 * einfach der davorliegende in der Liste, weil zwischendrin welche
 * übersprungen worden sein können.
 */
export function verlauf(funnel: Funnel, antworten: Antworten): Schritt[] {
  const weg: Schritt[] = [];
  let aktuell = ersterSchritt(funnel, antworten);
  // Sicherung gegen einen Ringschluss, den die Veröffentlichungsprüfung
  // eigentlich verhindert. Eine Endlosschleife im Browser wäre schlimmer als
  // ein abgeschnittener Weg.
  let schutz = 0;

  while (aktuell && schutz < funnel.steps.length * 2) {
    weg.push(aktuell);
    aktuell = naechsterSchritt(funnel, aktuell.id, antworten);
    schutz += 1;
  }

  return weg;
}

/**
 * Wie weit ist der Nutzer, in Prozent. Für die Fortschrittsanzeige.
 *
 * `anteil` sagt, wie weit der aktuelle Schritt selbst schon erledigt ist, von
 * 0 bis 1. Das braucht der Kontaktschritt, der sich auf mehrere Bildschirme
 * verteilt: Ohne diesen Anteil stünde der Balken über vier Bildschirme hinweg
 * still, und genau dort, wo die meisten abspringen, sähe es aus, als ginge es
 * nicht voran. Ohne Angabe gilt 1, dann verhält sich die Anzeige wie zuvor.
 */
export function fortschritt(
  funnel: Funnel,
  aktuelleId: string,
  antworten: Antworten,
  anteil = 1,
): number {
  const weg = verlauf(funnel, antworten);
  const position = weg.findIndex((s) => s.id === aktuelleId);
  if (position === -1 || weg.length === 0) return 0;

  const begrenzt = Math.min(1, Math.max(0, anteil));

  // Der erste Schritt zeigt bewusst nicht null Prozent: Ein leerer Balken
  // wirkt, als hätte man noch gar nichts geschafft.
  return Math.round(((position + begrenzt) / weg.length) * 100);
}

/** Schritte, die keine Frage sind und deshalb nicht mitgezählt werden. */
const KEINE_FRAGE = new Set(["information", "contact", "consent"]);

/**
 * Die wievielte Frage ist das, von wie vielen - für "Frage 2 von 5".
 *
 * Gezählt wird auf dem Weg, den die bisherigen Antworten ergeben. Überspringt
 * eine Antwort Fragen, sinkt die Gesamtzahl mit, statt dass eine Nummer
 * ausfällt. Zwischenseite, Einwilligung und Kontaktdaten zählen nicht: Sie
 * sind keine Frage, und "Frage 7 von 7" auf dem Kontaktformular würde
 * versprechen, dass danach Schluss ist, obwohl der Bildschirm mehrere Teile
 * hat.
 *
 * `nummer` ist null auf einem Schritt, der keine Frage ist.
 */
export function fragenzaehler(
  funnel: Funnel,
  aktuelleId: string,
  antworten: Antworten,
): { nummer: number | null; gesamt: number } {
  const fragen = verlauf(funnel, antworten).filter((s) => !KEINE_FRAGE.has(s.type));
  const position = fragen.findIndex((s) => s.id === aktuelleId);
  return { nummer: position === -1 ? null : position + 1, gesamt: fragen.length };
}
